import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import { observer }              from 'mobx-react-lite';
import { PictureOutlined }       from '@ant-design/icons';
import { FontColorsOutlined }    from '@ant-design/icons';
import SidebarTabs               from '@/TokamapEditor/ux/AreaSidebar/SidebarTabs';
import BadgeSettings             from '@/TokamapEditor/ux/AreaSidebar/BadgeSettings';
import ThemeImageFields          from '@/TokamapEditor/ux/ThemeImageFields';
import { ThemeImageSourceField } from '@/TokamapEditor/ux/ThemeImageFields';
import ThemeTextFields           from '@/TokamapEditor/ux/ThemeTextFields';
import { ThemeTextContentField } from '@/TokamapEditor/ux/ThemeTextFields';
import HelpTip                   from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import { BADGE_MODES_HELP }      from '@/TokamapEditor/ux/AreaSidebar/BadgeDisplayField';
import { textPositionOf }        from '@/TokamapEditor/helpers/shapeBadge';
import { ImposedField }          from '@/TokamapEditor/ux/ThemeFields';
import type { BadgeField }       from '@/TokamapEditor/ux/AreaSidebar/BadgeSettings';
import type { ShapeBadge }       from '@tokamap/core';
import themeStore                from '@/TokamapEditor/stores/themeStore';
import { Theme }                 from '@/TokamapEditor/stores/themeStore';
import useDesignTokens           from '@/hooks/useDesignTokens';
import type { ThemeFieldTarget } from '@/TokamapEditor/helpers/themeImposed';

/** La valeur que prend un réglage du marqueur tout juste imposé : le défaut du marqueur, un gris neutre pour la couleur. */
const BADGE_IMPOSED_START: Required<Pick<ShapeBadge, BadgeField>> = {
	style: 'disc',
	size: 'medium',
	shape: 'round',
	color: '#9e9e9e',
	anchor: 'center',
	showText: true,
	textPosition: 'below',
	minZoomOffset: 1,
};

const ThemeContentSection = observer(({ theme, save, focus }: {
	theme: Theme;
	save: (task: Promise<unknown>) => Promise<void>;
	focus?: ThemeFieldTarget;
}) => {
	const [tab, setTab] = React.useState<'image' | 'text'>(focus?.tab ?? 'image');
	const tokens = useDesignTokens();
	const badge = theme.badge || undefined;
	const setBadge = (patch: Partial<ShapeBadge>) => save(themeStore.setThemeBadge(theme.id, { ...badge, ...patch }));

	return (
		<View gap="lg">
			<ImposedField
				field="badge"
				label="Image et texte"
				imposed={theme.badge !== undefined}
				onImpose={() => save(themeStore.setThemeBadge(theme.id, false))}
				onRelease={() => save(themeStore.setThemeBadge(theme.id, undefined))}
			>
				<BadgeSettings
					label={<>Affichage<HelpTip>{BADGE_MODES_HELP}</HelpTip></>}
					modes={['Dans la forme', 'En marqueur']}
					mode={badge ? 'marker' : 'inside'}
					values={{
						style: badge?.style ?? 'disc',
						size: badge?.size ?? 'medium',
						shape: badge?.shape ?? 'round',
						anchor: badge?.anchor ?? 'center',
						showText: badge?.showText ?? true,
						textPosition: textPositionOf(badge),
						color: badge?.color,
						minZoomOffset: badge?.minZoomOffset,
					}}
					onMode={mode => save(themeStore.setThemeBadge(theme.id, mode === 'marker' ? {} : false))}
					onChange={setBadge}
					frame={(field, label, control, help) => (
						<ImposedField
							key={field}
							field={`badge.${field}`}
							label={label}
							imposed={badge?.[field] !== undefined}
							onImpose={() => setBadge({ [field]: field === 'textPosition' ? textPositionOf(badge) : BADGE_IMPOSED_START[field] })}
							onRelease={() => setBadge({ [field]: undefined })}
						>
							<View gap={4}>
								{control}
								{!!help && <View size={12} color={tokens.text.muted}>{help}</View>}
							</View>
						</ImposedField>
					)}
				/>
			</ImposedField>

			<View h={1} bg={tokens.border.default} />

			{badge ? (
				<View gap="md">
					<ThemeImageSourceField theme={theme} save={save} label="Image du marqueur" />

					<ThemeTextContentField theme={theme} save={save} label="Texte du marqueur" />

					<View size={12} color={tokens.text.muted}>
						Non imposés, l'image et le texte restent ceux de chaque forme. Le style du marqueur et l'affichage
						de son texte se règlent ci-dessus.
					</View>
				</View>
			) : (
				<View gap="md">
					<SidebarTabs
						value={tab}
						onChange={setTab}
						options={[
							{ value: 'image', label: 'Image', icon: <PictureOutlined /> },
							{ value: 'text', label: 'Texte', icon: <FontColorsOutlined /> },
						]}
					/>

					{tab === 'image' ? <ThemeImageFields theme={theme} save={save} /> : <ThemeTextFields theme={theme} save={save} initialScope={focus?.scope} />}
				</View>
			)}
		</View>
	);
});

export default ThemeContentSection;
